<template>
  <button
    type="button"
    class="share-link-chip rounded-pill"
    :class="{ 'is-copied': linkCopied }"
    :title="url"
    :aria-label="$t('common.copyLink')"
    :disabled="disabled"
    @click="copyLink"
  >
    <v-icon
      small
      class="share-link-icon"
    >
      {{ linkCopied ? 'mdi-check' : 'mdi-link-variant' }}
    </v-icon>
    <span class="text-caption share-link-url">{{ displayUrl }}</span>
    <span class="text-caption font-weight-bold share-link-action">
      {{ linkCopied ? $t('common.linkCopied') : $t('common.copy') }}
    </span>
  </button>
</template>

<script>
// How long the "copied" confirmation state stays visible.
const COPIED_RESET_MS = 2500;

export default {
  name: 'ShareLinkChip',
  props: {
    url: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      linkCopied: false,
    };
  },
  computed: {
    // The protocol carries no meaning for a human; show host + path and let
    // CSS ellipsize the middle on narrow screens.
    displayUrl() {
      return this.url.replace(/^https?:\/\//, '');
    },
  },
  beforeDestroy() {
    clearTimeout(this.copyTimeout);
  },
  methods: {
    async copyLink() {
      // Only confirm after the copy actually succeeded: clipboard access can
      // be denied or unavailable (insecure contexts, permissions). Fall back
      // to the legacy textarea trick before giving up.
      let copied = false;
      if (navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(this.url);
          copied = true;
        } catch {
          copied = false;
        }
      }
      if (!copied) {
        copied = this.legacyCopy();
      }
      if (!copied) return;

      this.linkCopied = true;
      clearTimeout(this.copyTimeout);
      this.copyTimeout = setTimeout(() => {
        this.linkCopied = false;
      }, COPIED_RESET_MS);
    },
    legacyCopy() {
      const textarea = document.createElement('textarea');
      textarea.value = this.url;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      let succeeded = false;
      try {
        succeeded = document.execCommand('copy');
      } catch {
        succeeded = false;
      }
      document.body.removeChild(textarea);
      return succeeded;
    },
  },
};
</script>

<style lang="scss" scoped>
// Quiet document-furniture: reads as metadata until hovered, confirms in
// the app's success green when copied. All colors come from app tokens.
.share-link-chip {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 6px 14px;
  border: 1px solid $book-grey-lighter;
  background: $book-grey-lightest;
  color: $book-grey-dark;
  transition: border-color 0.2s $book-ease-out, background 0.2s $book-ease-out;

  &:hover,
  &:focus-visible {
    border-color: var(--v-primary-base);
    background: var(--v-background-base);

    .share-link-action {
      color: var(--v-primary-base);
    }
  }

  &.is-copied {
    border-color: rgba($book-green, 0.4);
    background: rgba($book-green, 0.08);

    .share-link-icon,
    .share-link-action {
      color: $book-green !important;
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
    pointer-events: none;
  }
}

.share-link-icon {
  color: inherit !important;
  margin-right: 8px;
  flex-shrink: 0;
}

.share-link-url {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  min-width: 0;
}

.share-link-action {
  margin-left: 12px;
  padding-left: 12px;
  border-left: 1px solid $book-grey-lighter;
  flex-shrink: 0;
  white-space: nowrap;
}
</style>
